import * as React from 'react';
import { FieldWrapper, FieldWrapperPassThroughProps } from './FieldWrapper';

export type PriceInputProps = React.InputHTMLAttributes<HTMLInputElement> &
  FieldWrapperPassThroughProps & {
    className?: string;
    value: number;
    onChange: (value: number) => void;
    isAllowZero?: boolean;
  };

const PriceInput = React.forwardRef<HTMLInputElement, PriceInputProps>(
  ({ className, label, errorMessage, value, onChange, isAllowZero, ...props }, ref) => {
    const inputId = label;
    const [displayValue, setDisplayValue] = React.useState<string>('');

    React.useEffect(() => {
      if (value !== undefined && value !== null) {
        setDisplayValue(formatDisplay(value.toString()));
      }
    }, [value]);

    const formatDisplay = (str: string): string => {
      if (!str) return '';
      if (!isAllowZero && str === '0') return '';

      const [intPart, decimalPart] = str.split('.');
      const intFormatted = Number(intPart).toLocaleString();
      return decimalPart !== undefined ? `$ ${intFormatted}.${decimalPart}` : `$ ${intFormatted}`;
    };

    const parseInput = (str: string): number => {
      const cleaned = str.replace(/[^0-9.]/g, '');
      if (!cleaned) return 0;
      const parsed = parseFloat(cleaned);
      return isNaN(parsed) ? 0 : parsed;
    };

    const onKeyDownCurrency = (e: React.KeyboardEvent<HTMLInputElement>) => {
      const inputValue = (e.target as HTMLInputElement).value.replace(/\$/g, '').replace(/,/g, '');
      const key = e.key;

      if (['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', 'Home', 'End', 'Enter'].includes(key)) {
        return;
      }

      if (key === '.') {
        if (inputValue.includes('.')) {
          e.preventDefault();
        }
        return;
      }

      if (!/[0-9]/.test(key)) {
        e.preventDefault();
        return;
      }

      if (inputValue === '0' && key !== '.') {
        e.preventDefault();
        return;
      }

      if (inputValue.startsWith('0') && !inputValue.startsWith('0.') && key !== '.') {
        e.preventDefault();
        return;
      }

      const parts = inputValue.split('.');
      if (parts.length === 2 && parts[1].length >= 2) {
        const cursorPos = (e.target as HTMLInputElement).selectionStart || 0;
        if (cursorPos > inputValue.indexOf('.')) {
          e.preventDefault();
        }
      }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const inputValue = e.target.value.replace(/\$/g, '').trimStart();

      if (inputValue.trim() === '') {
        setDisplayValue('');
        onChange(0);
        return;
      }

      if (inputValue === '0' && !isAllowZero) {
        // If they manually type "0", clear it too
        setDisplayValue('');
        onChange(0);
        return;
      }

      if (inputValue.endsWith('.')) {
        const numericPart = inputValue.slice(0, -1).replace(/,/g, '');
        setDisplayValue(formatDisplay(numericPart) + '.');
        onChange(Number(numericPart || 0));
        return;
      }

      const [intPartRaw, decimalPartRaw] = inputValue.split('.');
      const decimalPart = decimalPartRaw?.slice(0, 2) ?? '';
      const formatted = Number(intPartRaw.replace(/,/g, '') || 0).toLocaleString();

      setDisplayValue(decimalPart ? `$ ${formatted}.${decimalPart}` : `$ ${formatted}`);

      const parsedValue = parseInput(`${intPartRaw}.${decimalPart}`);
      onChange(parsedValue);
    };

    return (
      <FieldWrapper
        label={label}
        errorMessage={errorMessage}
        float={true}
        className={className}
        htmlFor={inputId}
      >
        <input
          ref={ref}
          id={inputId}
          type="text"
          aria-required="true"
          placeholder={label}
          aria-invalid={errorMessage ? 'true' : 'false'}
          className={`iLendingCDX-text-field__float ${errorMessage && 'iLendingCDX-text-field__float--error'}`}
          value={displayValue}
          onChange={handleChange}
          onKeyDown={onKeyDownCurrency}
          {...props}
        />
      </FieldWrapper>
    );
  }
);

export { PriceInput };
